// Palworld graded (PSA / BGS / CGC) card price hub.
//
// No Palworld competitor tracks slabbed copies, so certified pricing is its own
// indexable layer: this hub explains how graded value is formed and links one
// page per chase printing. Raw prices live on /prices; this layer is certified
// copies only and never blends into the raw market price or the index.
import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { GRADING_SOURCES } from "@/lib/prices-shared";
import { GRADED_CARDS, gradedPath } from "@/lib/graded-pages";

const URL = "https://palstars.com/graded-card-prices";
const TITLE = "Palworld Graded Card Prices: PSA, BGS & CGC";
const DESCRIPTION =
  "Palworld TCG graded card prices: PSA 10, BGS 9.5 and CGC slab values for every chase printing, the graded premium over raw, and population-report links.";

const FAQ: FaqItem[] = [
  {
    q: "How much is a PSA 10 Palworld card worth?",
    a: "A PSA 10 Palworld card is worth several times its raw price, and the multiple grows with rarity: commons gain little because grading fees exceed the uplift, while OSR and SSP chase printings routinely trade at three to ten times raw in a PSA 10. Open the card's graded page here for its current certified asking range next to the raw market price.",
  },
  {
    q: "Is it worth grading Palworld TCG cards?",
    a: "Grade a card when the graded price minus the raw price clearly exceeds grading and shipping costs, and only when the copy is genuinely gem-mint. That is realistic for OSR, SSP, SSS and TSP chase printings and almost never true for base commons and uncommons.",
  },
  {
    q: "Which grading company is best for Palworld cards?",
    a: "PSA has the deepest buyer recognition, so PSA 10 slabs resell fastest. Beckett prints subgrades and a Black Label 10 that carries the highest ceiling on a perfect copy, and CGC returns are typically cheaper and quicker with smaller populations on modern releases.",
  },
  {
    q: "Do graded prices affect the Pal Stars price index?",
    a: "No. Certified slabs are tracked as their own series and never blend into the raw market price, the raw low/high band or the base-1,000 index. That keeps a single slab sale from inflating a raw card's value.",
  },
  {
    q: "How do I check a Palworld card's graded population?",
    a: "Search the exact printed card number in the grading company's population report — PSA, Beckett and CGC each publish one. The population at the top grade is the scarcity half of a slab's value: a low PSA 10 count with steady demand is what sustains a premium.",
  },
];

export const Route = createFileRoute("/graded-card-prices/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld graded card prices — PSA, BGS & CGC" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld graded card prices — PSA, BGS & CGC" },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Graded Card Prices", "/graded-card-prices"],
      ]),
      webPageSchema({ name: "Palworld Graded Card Prices", description: DESCRIPTION, path: "/graded-card-prices" }),
      faqSchema(FAQ, URL),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Palworld graded card price pages",
          numberOfItems: GRADED_CARDS.length,
          itemListElement: GRADED_CARDS.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://palstars.com${gradedPath(c.code)}`,
            name: `${c.code} ${c.name} graded price`,
          })),
        }),
      },
    ],
  }),
  component: GradedHub,
});

function GradedHub() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld TCG · certified copies</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld Graded Card Prices</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          A graded Palworld card is worth a multiple of its raw price, and the multiple rises with rarity: chase
          printings such as OSR, SSP, SSS and TSP commonly trade at three to ten times raw in a PSA 10, while base
          commons gain less than the grading fee. Every page below shows the certified asking range next to the raw
          market price, the graded premium as a multiple, and population-report links so you can check scarcity before
          paying a slab premium. Certified quotes are tracked separately and never blended into raw prices or the index.
        </p>
      </header>

      <AffiliateDisclosure className="mt-6" />

      <section className="mt-8">
        <h2 className="text-xl font-bold">Graded price pages by chase printing</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {GRADED_CARDS.map((c) => (
            <Link
              key={c.code}
              to="/graded-card-prices/$code"
              params={{ code: c.code }}
              className="glass flex items-center gap-3 rounded-2xl p-3 hover:border-primary/50"
            >
              <CardImage
                src={c.image}
                alt={`${c.name} ${c.code} Palworld card`}
                width={44}
                height={62}
                className="h-[62px] w-11 rounded object-cover"
              />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">
                  {c.name} — PSA & BGS prices
                </span>
                <span className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="font-mono">{c.code}</span>
                  <RarityBadge code={c.code} />
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-bold">Population reports</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {GRADING_SOURCES.map((g) => (
            <article key={g.id} className="glass rounded-2xl p-4">
              <h3 className="text-sm font-bold">{g.company}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{g.scale}</p>
              <p className="mt-2 text-xs text-muted-foreground">{g.note}</p>
              <a
                href={g.pop}
                target="_blank"
                rel="nofollow ugc noopener noreferrer"
                className="mt-3 inline-block text-xs font-semibold text-primary hover:underline"
              >
                Open the {g.company} population report
              </a>
            </article>
          ))}
        </div>
      </section>

      <InternalLinkBlock path="/prices" />
      <FaqSection items={FAQ} />
    </div>
  );
}
